import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { ImageIcon, VideoIcon, ChevronDown } from 'lucide-react'
import { PlatformPostType, getSupportedMediaTypes, MediaType } from '@/types/content.types'

interface EnhanceMediaDropdownProps {
    onEnhanceMedia: (mediaType: 'image' | 'video') => void
    enhancingMedia?: boolean
    className?: string
    variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
    size?: "default" | "sm" | "lg" | "icon"
    platformPostType?: PlatformPostType // Optional: filters media options based on post type
}

/**
 * Dropdown component for enhancing content with different media types
 * If platformPostType is provided, only shows media options supported by that post type
 */
export function EnhanceMediaDropdown({
    onEnhanceMedia,
    enhancingMedia = false,
    className,
    variant = "outline",
    size = "default",
    platformPostType
}: EnhanceMediaDropdownProps) {
    const handleMediaTypeSelect = (mediaType: 'image' | 'video') => {
        onEnhanceMedia(mediaType)
    }

    // Determine which media types to show based on platformPostType
    const supportedMediaTypes: MediaType[] = platformPostType
        ? getSupportedMediaTypes(platformPostType)
        : ['image', 'video']; // Default: show all if no post type specified

    const shouldShowImages = supportedMediaTypes.includes('image');
    const shouldShowVideos = supportedMediaTypes.includes('video');
    const shouldShowAnyMedia = !supportedMediaTypes.includes('none');

    // If no media is supported, don't render the component
    if (!shouldShowAnyMedia || (!shouldShowImages && !shouldShowVideos)) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={variant}
                    size={size}
                    className={className}
                    disabled={enhancingMedia}
                >
                    <ImageIcon className="h-4 w-4 mr-2" />
                    {enhancingMedia ? 'Enhancing...' : 'Enhance Media'}
                    {!enhancingMedia && <ChevronDown className="h-4 w-4 ml-2" />}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                {shouldShowImages && (
                    <DropdownMenuItem
                        onClick={() => handleMediaTypeSelect('image')}
                        disabled={enhancingMedia}
                    >
                        <ImageIcon className="h-4 w-4 mr-2" />
                        Enhance with Images
                    </DropdownMenuItem>
                )}
                {shouldShowVideos && (
                    <DropdownMenuItem
                        onClick={() => handleMediaTypeSelect('video')}
                        disabled={enhancingMedia}
                    >
                        <VideoIcon className="h-4 w-4 mr-2" />
                        Enhance with Videos
                    </DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
} 